import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { EmojiEvents, ListAlt } from "@mui/icons-material";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { postData } from "../../Utils/Http/api";
import RankingCasosGestor from "./RankingCasosGestor";
import { CabeceraOrdenable, ordenarFilas, useOrdenTabla } from "./informeDisparadorComun";

// Oro, plata y bronce para los tres primeros puestos (solo si sumaron puntos).
const COLOR_PODIO = { 1: "#f9a825", 2: "#9e9e9e", 3: "#a1693a" };

const CRITERIOS = {
  tomados: { posicion: "posicionPorTomados", puntos: "puntosTomados", casos: "casosTomados", otros: "puntosFinalizados" },
  finalizados: { posicion: "posicionPorFinalizados", puntos: "puntosFinalizados", casos: "casosFinalizados", otros: "puntosTomados" },
};

const formatearDia = (iso) => {
  const [anio, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${anio}`;
};

const numero = (valor) => (valor ?? 0).toLocaleString("es-AR");

// Tabla de posiciones del Disparador: suma de los puntos de los casos que se le asignaron a cada gestor en el período de la
// pantalla del informe. Se ordena por los puntos de los casos tomados o por los de los casos finalizados.
function RankingDisparador({ open, onClose, desde, hasta }) {
  const [ranking, setRanking] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [criterio, setCriterio] = useState("tomados");
  const orden = useOrdenTabla();
  // Gestor cuyos casos se están viendo en el segundo popup.
  const [gestorDetalle, setGestorDetalle] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelado = false;
    const cargar = async () => {
      setCargando(true);
      setError("");
      setRanking(null);
      try {
        const res = await postData("/InformeDisparadorRanking", { desde, hasta });
        if (!cancelado) setRanking(res?.value ?? null);
      } catch (ex) {
        if (!cancelado) setError(ex?.detail || ex?.message || "No se pudo armar la tabla de posiciones");
      } finally {
        if (!cancelado) setCargando(false);
      }
    };
    cargar();
    return () => {
      cancelado = true;
    };
  }, [open, desde, hasta]);

  const c = CRITERIOS[criterio];

  // Las filas vienen en el orden de la tabla de posiciones (según el criterio elegido); al tocar el título de una columna se
  // ordenan por ella, y el tercer toque vuelve a ese orden. La posición (#) no cambia: sigue siendo la del criterio.
  const filasOrdenadas = useMemo(() => {
    const posiciones = ranking?.posiciones ?? [];
    return [...posiciones].sort(
      (a, b) => a[c.posicion] - b[c.posicion] || b[c.otros] - a[c.otros] || a.nombre.localeCompare(b.nombre)
    );
  }, [ranking, c]);

  const valoresOrden = useMemo(
    () => ({
      posicion: (f) => f[c.posicion],
      gestor: (f) => f.nombre,
      tomados: (f) => f.puntosTomados,
      finalizados: (f) => f.puntosFinalizados,
      liberados: (f) => f.casosLiberados,
      promedio: (f) => (f.casosFinalizados > 0 ? f.promedioPuntosPorCaso : null),
    }),
    [c]
  );

  const filas = useMemo(() => ordenarFilas(filasOrdenadas, orden[0], valoresOrden), [filasOrdenadas, orden, valoresOrden]);

  const maximo = useMemo(() => Math.max(0, ...filas.map((f) => f[c.puntos])), [filas, c]);

  const cabecera = (texto, clave, ancho, resaltada = false) => (
    <CabeceraOrdenable texto={texto} tabla={orden} clave={clave} ancho={ancho} resaltada={resaltada} />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Tabla de posiciones
        <MDTypography display="block" variant="caption" color="text" fontWeight="regular">
          Del {formatearDia(desde)} al {formatearDia(hasta)}
        </MDTypography>
      </DialogTitle>
      <DialogContent dividers>
        <MDBox display="flex" flexDirection="column" gap={2}>
          <MDTypography variant="caption" color="text">
            Suma los puntos de los casos que el sistema le asignó a cada gestor, con el puntaje que tenía el caso al
            asignarlo. <strong>Tomados</strong> son todos los asignados en el período, incluso los liberados y los que
            siguen en curso; <strong>finalizados</strong>, solo aquellos en los que el gestor ya cargó el evento. Los
            empates comparten posición.
          </MDTypography>

          {ranking?.incluyeHistorico && (
            <Alert severity="info">
              Estos puntos incluyen el <strong>trabajo anterior al Disparador</strong>, que se cargó una sola vez: cada caso en el que el
              gestor cargó eventos suma una vez, con el puntaje que tiene hoy ese caso, en la fecha de su último evento. Ese trabajo
              cuenta como finalizado y no entra en los tiempos ni en el listado de propuestas del informe.
            </Alert>
          )}

          <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <MDTypography variant="caption" fontWeight="bold" color="text">
              Ordenar por puntos de los casos
            </MDTypography>
            <ToggleButtonGroup size="small" exclusive value={criterio} onChange={(e, valor) => valor && setCriterio(valor)}>
              <ToggleButton value="tomados">Tomados</ToggleButton>
              <ToggleButton value="finalizados">Finalizados</ToggleButton>
            </ToggleButtonGroup>
          </MDBox>

          {error && <Alert severity="error">{error}</Alert>}

          {cargando && (
            <MDBox display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </MDBox>
          )}

          {!cargando && ranking && (
            <TableContainer sx={{ maxHeight: "55vh" }}>
              <Table size="small" stickyHeader>
                <TableHead sx={{ display: "table-header-group", p: 0 }}>
                  <TableRow>
                    {cabecera("#", "posicion", "7%")}
                    {cabecera("Gestor", "gestor")}
                    {cabecera("Puntos tomados", "tomados", "24%", criterio === "tomados")}
                    {cabecera("Puntos finalizados", "finalizados", "24%", criterio === "finalizados")}
                    {cabecera("Liberados", "liberados", "10%")}
                    {cabecera("Promedio por caso", "promedio", "12%")}
                    {cabecera("Casos", undefined, "7%")}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filas.map((f) => {
                    const posicion = f[c.posicion];
                    const podio = f[c.puntos] > 0 ? COLOR_PODIO[posicion] : undefined;
                    return (
                      <TableRow key={f.idUsuario} hover>
                        <TableCell>
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            <MDTypography variant="button" fontWeight="bold" color="dark">
                              {posicion}
                            </MDTypography>
                            {podio && <EmojiEvents fontSize="small" sx={{ color: podio }} aria-label={`Puesto ${posicion}`} />}
                          </MDBox>
                        </TableCell>
                        <TableCell>
                          <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <MDTypography variant="button" fontWeight="bold">
                              {f.nombre}
                            </MDTypography>
                            {!f.activo && <Chip size="small" color="error" label="Desactivado" />}
                          </MDBox>
                        </TableCell>
                        <TableCell>
                          <MDBox lineHeight={1.2}>
                            <MDTypography variant="button" fontWeight={criterio === "tomados" ? "bold" : "regular"}>
                              {numero(f.puntosTomados)}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              {numero(f.casosTomados)} {f.casosTomados === 1 ? "caso" : "casos"}
                            </MDTypography>
                            {criterio === "tomados" && (
                              <LinearProgress
                                variant="determinate"
                                value={maximo > 0 ? Math.max(0, (f.puntosTomados / maximo) * 100) : 0}
                                sx={{ mt: 0.5, height: 5, borderRadius: 3 }}
                              />
                            )}
                          </MDBox>
                        </TableCell>
                        <TableCell>
                          <MDBox lineHeight={1.2}>
                            <MDTypography variant="button" fontWeight={criterio === "finalizados" ? "bold" : "regular"}>
                              {numero(f.puntosFinalizados)}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              {numero(f.casosFinalizados)} {f.casosFinalizados === 1 ? "caso" : "casos"}
                            </MDTypography>
                            {criterio === "finalizados" && (
                              <LinearProgress
                                variant="determinate"
                                value={maximo > 0 ? Math.max(0, (f.puntosFinalizados / maximo) * 100) : 0}
                                sx={{ mt: 0.5, height: 5, borderRadius: 3 }}
                              />
                            )}
                          </MDBox>
                        </TableCell>
                        <TableCell>
                          <MDTypography variant="caption" color="text">
                            {f.casosLiberados}
                          </MDTypography>
                        </TableCell>
                        <TableCell>
                          <MDTypography variant="caption" color="text">
                            {f.casosFinalizados > 0
                              ? (f.promedioPuntosPorCaso ?? 0).toLocaleString("es-AR", { maximumFractionDigits: 1 })
                              : "-"}
                          </MDTypography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Ver los casos y su último estado">
                            <IconButton size="small" color="info" aria-label={`Ver los casos de ${f.nombre}`} onClick={() => setGestorDetalle(f)}>
                              <ListAlt />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filas.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <MDTypography variant="button" color="text">
                          Ningún gestor tuvo casos asignados en el período.
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="info" onClick={onClose}>
          Cerrar
        </MDButton>
      </DialogActions>

      <RankingCasosGestor
        open={Boolean(gestorDetalle)}
        onClose={() => setGestorDetalle(null)}
        desde={desde}
        hasta={hasta}
        gestor={gestorDetalle}
        soloFinalizados={criterio === "finalizados"}
      />
    </Dialog>
  );
}

export default RankingDisparador;
